import React, { useEffect, useState } from 'react'
import UploadBook from './UploadBook'
import { useNavigate, useParams } from 'react-router';
import HTTPClient from 'services/HTTPClient';
import { useMutation, useQuery } from '@tanstack/react-query';
import Spinner from 'components/Spinner';
import ErrorIndicator from 'components/ErrorIndicator';
import { Input, Space } from 'antd';
import Button from 'components/Button';
import useGrade from 'components/useGrade';
import useTopic from 'components/useTopic';
import { showErrorToast, showSuccessToast } from 'utils';
import { Link } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';

type Props = {}

interface UploadedFile {
    id: string
    url: string
    name: string
}

interface Book {
    grade: string
    topic: string
    file: UploadedFile
    video?: UploadedFile
    id: number
}

export const useDocument = (documentID) => {
    return useQuery<Book>(['document', documentID], async () => {
        const url = `/api/books/${documentID}?populate=*`
        if (documentID) {
            let response = await HTTPClient.get().getJSONResult(url)
            return response.data
        }
        return null
    });
};

const useUpdateDocument = () => {
    return useMutation({
        mutationFn: async ({ documentID, grade, topic, file }: { documentID?: number, grade?: string, topic?: string, file?: any }) => {
            if (!documentID) {
                showErrorToast("documentID required!")
                return []
            }
            if (!topic) {
                showErrorToast("topic required!")
                return []
            }
            if (!grade) {
                showErrorToast("grade required!")
                return []
            }
            if (!file || !file?.name) {
                showErrorToast("file name is required!")
                return []
            }
            try {
                let fileUpdateResponse = await HTTPClient.get().postDataForJSONResult(`/api/upload/?id=${file?.id}`, { fileInfo: file })
                console.log(fileUpdateResponse)
                const url = `/api/books/${documentID}?populate=file`
                let data = {
                    grade,
                    topic,
                }
                console.log(data)
                let response = await HTTPClient.get().putDataForJSONResult(url, data)
                showSuccessToast("Done")
                return response
            } catch (error: any) {
                showErrorToast(error.message)
                return []
            }
        }
    })
}

export default function EditBookPage({ }: Props) {
    const [showUpload, setShowUpload] = useState(false)
    let { document_id } = useParams();
    const navigate = useNavigate();
    let { data, error, isLoading, refetch } = useDocument(document_id)
    const { isLoading: isUpdatingBook, mutateAsync: updateDocument } = useUpdateDocument()
    let [grade, gradeSelectorRenderer] = useGrade(data?.grade);
    let [topic, topicSelectorRenderer] = useTopic(data?.topic);
    let [name, setName] = useState(data?.file?.name)

    useEffect(() => {
        if (name !== data?.file?.name) {
            setName(data?.file?.name)
        }
    }, [data?.file?.name, name])


    console.log(data)

    if (isLoading) {
        return <Spinner className='mx-auto mt-2' />
    }

    if (error) {
        return <ErrorIndicator error={error} />
    }

    return (
        <div className='mx-0 px-5 py-5' style={{ height: "100vh", overflow: "auto" }}>
            <div className='mb-3'>
                <Button
                    variant='is-light'
                    className='py-1'
                    onClick={() => navigate(-1)}
                >
                    <LeftOutlined className='mr-2' />
                    Back
                </Button>
            </div>
            {(data?.file?.url || data?.video?.url) && !showUpload
                ? <div>
                    <h3 className='is-size-3 mb-5'>
                        <strong>Edit book details</strong>
                    </h3>
                    {data?.video?.url && <video className='w-100 mb-3' controls src={data?.video?.url}></video>}
                    <form onSubmit={async (event) => {
                        event.preventDefault()
                        await updateDocument({
                            documentID: data?.id,
                            grade,
                            topic,
                            file: {
                                ...data?.file,
                                name
                            }
                        })
                    }}>
                        <div className='book-edit-grid'>
                            Book
                            <Space.Compact style={{ width: '100%' }}>
                                <Input disabled value={data?.file?.url} />
                                <Button type="button" onClick={() => setShowUpload(true)}>Change</Button>
                            </Space.Compact>
                            Name
                            <div className='mt-4'>
                                <Input value={name} onChange={(event) => {
                                    if (!data?.file) {
                                        showErrorToast("Please wait for the video to be processed")
                                        setName("")
                                        return
                                    }
                                    //@ts-ignore
                                    data.file.name = event.target.value
                                    setName(event.target.value)
                                }} />
                            </div>
                            Grade
                            <div className='mt-4'>
                                {gradeSelectorRenderer()}
                            </div>
                            Topic
                            <div className='mt-4'>
                                {topicSelectorRenderer()}
                            </div>
                        </div>
                        <div className='end mt-5'>
                            <Link to={`/document/${data.id}`}>
                                <Button
                                    type='button'
                                    variant="is-light"
                                    className='mr-3'>
                                    Chat with PDF
                                </Button>
                            </Link>
                            <Button
                                isLoading={isUpdatingBook}
                                loadingText="Submitting"
                                type='submit'>
                                Submit
                            </Button>
                        </div>
                    </form>
                </div>
                : <UploadBook
                    onUploadComplete={() => {
                        setShowUpload(false)
                        refetch()
                    }}
                    bookID={data?.id}
                />
            }
        </div>
    )
}